<script setup lang="ts">
import type { ProTableColumn } from './types'
import { FaInput, FaNumberField } from '@fantastic-admin/components'
import VxeUITable from 'vxe-table'

defineOptions({
  name: 'FaProTableRecursiveColumn',
})

const props = defineProps<{
  column: ProTableColumn
  slotsMap: Record<string, ((props: any) => any) | undefined>
  renderableContent: Component
  resolveHeaderSlotName: (column: ProTableColumn) => string
  resolveColumnSlotName: (column: ProTableColumn) => string
  shouldRenderComplexContent: (column: ProTableColumn) => boolean
  renderCellContent: (scope: { row: Record<string, any>, $index: number }, column: ProTableColumn) => any
  resolvePlainCellContent: (scope: { row: Record<string, any>, $index: number }, column: ProTableColumn) => unknown
  resolveEditorDisabled: (row: Record<string, any>, column: ProTableColumn, rowIndex: number) => boolean
  resolveColumnField: (column: ProTableColumn) => string | undefined
  treeColumnKey?: string
  rowDragColumnKey?: string
  indexOffset?: number
}>()

const VxeColumn = VxeUITable.VxeColumn
const VxeColgroup = VxeUITable.VxeColgroup

const headerSlotName = computed(() => props.resolveHeaderSlotName(props.column))
const cellSlotName = computed(() => props.resolveColumnSlotName(props.column))
const hasChildren = computed(() => Boolean(props.column.children?.length))
const columnComponent = computed(() => hasChildren.value ? VxeColgroup : VxeColumn)
const isSelection = computed(() => props.column.type === 'selection')
const isIndex = computed(() => props.column.type === 'index')
const isExpand = computed(() => props.column.type === 'expand')
const columnType = computed(() => isSelection.value ? 'checkbox' : isIndex.value ? 'seq' : isExpand.value ? 'expand' : undefined)
const columnFixed = computed(() => props.column.fixed === true ? 'left' : props.column.fixed || undefined)
const isTreeNode = computed(() => props.treeColumnKey === String(props.column.key ?? props.column.prop ?? ''))
const isRowDragColumn = computed(() => props.rowDragColumnKey === String(props.column.key ?? props.column.prop ?? ''))
const columnFilters = computed(() => props.column.filters?.map(item => ({
  label: item.text,
  value: item.value,
  checked: props.column.filteredValue?.some(value => String(value) === String(item.value)) ?? false,
})))

function normalizeScope(scope: any) {
  return {
    ...scope,
    $index: scope.$rowIndex ?? scope.rowIndex ?? 0,
  }
}

function filterMethod({ value, row }: { value: any, row: Record<string, any> }) {
  return props.column.filterMethod?.(value, row, props.column) ?? true
}

function setValueByPath(row: Record<string, any>, path: string, value: unknown) {
  const keys = path.split('.')
  const lastKey = keys.pop()
  if (!lastKey) {
    return
  }
  const target = keys.reduce<Record<string, any>>((current, key) => {
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {}
    }
    return current[key]
  }, row)
  target[lastKey] = value
}

function resolveEditor(column: ProTableColumn) {
  return typeof column.editor === 'object' ? column.editor : {}
}

function resolveIndex(scope: any) {
  return typeof scope.seq === 'number' ? (props.indexOffset ?? 0) + scope.seq : scope.seq
}
</script>

<template>
  <component
    :is="columnComponent"
    :type="columnType"
    :field="resolveColumnField(column)"
    :title="column.label ?? (isIndex ? '#' : undefined)"
    :width="column.width ?? (isSelection ? 52 : isIndex ? 60 : undefined)"
    :min-width="column.minWidth"
    :fixed="columnFixed"
    :align="column.align ?? (isSelection || isIndex ? 'center' : undefined)"
    :header-align="column.headerAlign ?? column.align"
    :show-overflow="column.showOverflowTooltip ? 'title' : false"
    :sortable="!isExpand && Boolean(column.sortable)"
    :filters="columnFilters"
    :filter-method="column.filterMethod ? filterMethod : undefined"
    :filter-multiple="column.filterMultiple"
    :class-name="column.className"
    :header-class-name="column.labelClassName"
    :tree-node="isTreeNode"
    :drag-sort="isRowDragColumn"
    :edit-render="column.editor ? {} : undefined"
  >
    <template v-if="column.renderHeader" #header="scope">
      <component :is="renderableContent" :content="column.renderHeader({ column, $index: scope.$columnIndex ?? 0 })" />
    </template>

    <template v-else-if="headerSlotName" #header="scope">
      <component :is="slotsMap[headerSlotName]" v-if="slotsMap[headerSlotName]" v-bind="scope" :column="column" />
    </template>

    <FaProTableRecursiveColumn
      v-for="child in column.children"
      :key="String(child.key ?? child.prop ?? '')"
      :column="child"
      :slots-map="slotsMap"
      :renderable-content="renderableContent"
      :resolve-header-slot-name="resolveHeaderSlotName"
      :resolve-column-slot-name="resolveColumnSlotName"
      :should-render-complex-content="shouldRenderComplexContent"
      :render-cell-content="renderCellContent"
      :resolve-plain-cell-content="resolvePlainCellContent"
      :resolve-editor-disabled="resolveEditorDisabled"
      :resolve-column-field="resolveColumnField"
      :tree-column-key="treeColumnKey"
      :row-drag-column-key="rowDragColumnKey"
      :index-offset="indexOffset"
    />

    <template v-if="!hasChildren && !isSelection && !isExpand" #default="scope">
      <template v-if="isIndex">
        {{ resolveIndex(scope) }}
      </template>
      <component
        :is="slotsMap[cellSlotName]"
        v-else-if="cellSlotName && slotsMap[cellSlotName]"
        v-bind="normalizeScope(scope)"
        :column="column"
      />
      <component
        :is="renderableContent"
        v-else-if="shouldRenderComplexContent(column)"
        :content="renderCellContent(normalizeScope(scope), column)"
      />
      <template v-else>
        {{ resolvePlainCellContent(normalizeScope(scope), column) }}
      </template>
    </template>

    <template v-if="!hasChildren && column.editor" #edit="scope">
      <component
        :is="resolveEditor(column).type === 'number' ? FaNumberField : FaInput"
        :model-value="resolvePlainCellContent(normalizeScope(scope), column)"
        :placeholder="resolveEditor(column).placeholder"
        :disabled="resolveEditorDisabled(scope.row, column, scope.$rowIndex ?? scope.rowIndex ?? 0)"
        class="w-full"
        @update:model-value="setValueByPath(scope.row, String(column.prop ?? ''), $event)"
      />
    </template>

    <template v-if="isExpand" #content="scope">
      <component
        :is="slotsMap[cellSlotName]"
        v-if="cellSlotName && slotsMap[cellSlotName]"
        v-bind="normalizeScope(scope)"
        :column="column"
      />
      <component
        :is="renderableContent"
        v-else-if="shouldRenderComplexContent(column)"
        :content="renderCellContent(normalizeScope(scope), column)"
      />
    </template>
  </component>
</template>
